import { useDispatch } from 'react-redux';

import config from 'config';

import { StreamingPlatform } from 'types';
import { authorizeTwitch } from 'utils';
import { streamingPlatformsActions } from 'store/actions';
import { PlatformState, StreamingPlatformState } from 'store/slices/streamingPlatforms';
import { api, LoggerService } from 'services';

import useToast from './useToast';

export type UseStreamingAccountsProps = {
  onLinked?(): void;
};

const useStreamingAccounts = ({ onLinked }: UseStreamingAccountsProps) => {
  const dispatch = useDispatch();
  const toast = useToast();

  const handleOnLink = (
    platformKey: StreamingPlatform,
    state: Partial<StreamingPlatformState<StreamingPlatform, PlatformState>>
  ) => {
    dispatch(
      streamingPlatformsActions.updateStreamingPlatform({
        platformKey,
        platformState: {
          isLinked: true,
          ...state
        }
      })
    );

    onLinked?.();
  };

  const handleFacebook = () => {
    // @ts-ignore
    window.FB.login(
      response => {
        if (response.status === 'connected') {
          if (!response.authResponse.grantedScopes.includes('publish_video')) {
            toast.info('Permission denied. Would you please try again and grant access to publish the video?', {
              id: 'LINK_FACEBOOK'
            });
          } else {
            handleOnLink(StreamingPlatform.FACEBOOK, {
              isActive: true,
              isLinked: true,
              accessToken: response.authResponse.accessToken,
              state: {
                userId: response.authResponse.userID
              }
            });
            toast.info('Facebook account has been linked to LiveTalks', { id: 'LINK_FACEBOOK' });
          }
        } else {
          toast.info('User canceled login or did not authorize', { id: 'LINK_FACEBOOK' });
        }
      },
      {
        scope: ['email', 'publish_video'],
        return_scopes: true
      }
    );
  };

  const handleYoutube = async () => {
    try {
      const { accessToken, expiresAt } = await api.requests.youtube.getToken();

      handleOnLink(StreamingPlatform.YOUTUBE, {
        isLinked: true,
        isActive: true,
        accessToken,
        expiresAt
      });
    } catch (error) {
      LoggerService.log('LINK_YOUTUBE_ACCOUNT', error);

      if (error instanceof Error) {
        toast.error(error.message, { id: 'LINK_YOUTUBE' });
      }
    }
  };

  const handleTwitch = () => {
    authorizeTwitch({
      clientId: config.oauth.twitch.clientId,
      scope: ['channel:read:stream_key']
    }).then(accessToken => {
      handleOnLink(StreamingPlatform.TWITCH, {
        isLinked: true,
        isActive: true,
        accessToken
      });
    });
  };

  const link = (platformKey: StreamingPlatform) => {
    switch (platformKey) {
      case StreamingPlatform.FACEBOOK:
        handleFacebook();
        break;
      case StreamingPlatform.YOUTUBE:
        handleYoutube();
        break;
      case StreamingPlatform.TWITCH:
        handleTwitch();
        break;
      default:
        break;
    }
  };

  const enable = (platform: StreamingPlatform) => {
    dispatch(
      streamingPlatformsActions.updateStreamingPlatform({
        platformKey: platform,
        platformState: { isActive: true }
      })
    );
  };

  const disable = (platform: StreamingPlatform) => {
    dispatch(
      streamingPlatformsActions.updateStreamingPlatform({
        platformKey: platform,
        platformState: { isActive: false }
      })
    );
  };

  return { link, enable, disable };
};

export default useStreamingAccounts;
